import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Package, Tag, Cable, ArrowUp, ArrowDown, Circle,
  Building2, ShoppingCart, Edit2, EyeOff, Eye, ChevronRight,
} from 'lucide-react';
import {
  equipements, fournisseurs, commandes, attributsEquipements,
  formatMontant, formatDate,
} from '../data/mockData';

const typeColors = {
  Materiel: 'info',
  Prestation: 'warn',
  Licence: 'sky',
};

const renderSens = (sens) => {
  if (sens === 'amont') return <span className="badge badge-sky"><ArrowUp size={12} /> Amont</span>;
  if (sens === 'aval') return <span className="badge badge-info"><ArrowDown size={12} /> Aval</span>;
  return <span className="badge badge-neutral"><Circle size={10} /> Passif</span>;
};

const EquipementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const eq = equipements.find((e) => e.id === id);

  if (!eq) {
    return (
      <div className="page-container">
        <button className="back-link" onClick={() => navigate('/equipements')}>
          <ArrowLeft size={16} /> Retour aux equipements
        </button>
        <div className="empty-state">
          <Package size={28} />
          <p>Equipement introuvable.</p>
        </div>
      </div>
    );
  }

  const commandesLiees = commandes.filter((c) =>
    c.lignes.some((l) => l.equipementId === eq.id)
  );

  return (
    <div className="page-container">
      <button className="back-link" onClick={() => navigate('/equipements')}>
        <ArrowLeft size={16} /> Retour aux equipements
      </button>

      <div className="page-header">
        <div>
          <div className="page-title-row">
            <div className="page-icon-wrapper">
              <Package size={22} />
            </div>
            <div>
              <h1 className="page-title">{eq.libelle}</h1>
              <p className="page-subtitle mono">{eq.id}</p>
            </div>
          </div>
        </div>
        <div className="page-actions">
          <span className={`badge badge-${typeColors[eq.type]}`}><Tag size={11} /> {eq.type}</span>
          {renderSens(eq.sens)}
          <button className="btn-ghost"><Edit2 size={14} /> Modifier</button>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <header className="card-header">
            <div className="card-title">
              <Package size={18} />
              <h2>Informations generales</h2>
            </div>
          </header>
          <div className="card-body info-grid">
            <div>
              <span className="info-label">Libelle</span>
              <span className="info-value">{eq.libelle}</span>
            </div>
            <div>
              <span className="info-label">Type</span>
              <span className="info-value">{eq.type}</span>
            </div>
            <div>
              <span className="info-label">Marque</span>
              <span className="info-value">{eq.marque}</span>
            </div>
            <div>
              <span className="info-label">Modele</span>
              <span className="info-value">{eq.modele}</span>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <span className="info-label">Description</span>
              <span className="info-value">{eq.description}</span>
            </div>
          </div>
        </div>

        <div className="card">
          <header className="card-header">
            <div className="card-title">
              <Cable size={18} />
              <h2>Handles & sens de raccordement</h2>
            </div>
          </header>
          <div className="card-body info-grid">
            <div>
              <span className="info-label">Handles synoptique</span>
              <span className="info-value strong">{eq.handles.synoptique}</span>
            </div>
            <div>
              <span className="info-label">Handles detaille</span>
              <span className="info-value strong">{eq.handles.detaille}</span>
            </div>
            <div>
              <span className="info-label">Sens</span>
              <span className="info-value">{renderSens(eq.sens)}</span>
            </div>
            <div>
              <span className="info-label">Utilise par</span>
              <span className="info-value">{commandesLiees.length} commande(s)</span>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.25rem' }}>
        <header className="card-header">
          <div className="card-title">
            <Tag size={18} />
            <h2>Attributs</h2>
          </div>
          <button className="btn-link" onClick={() => navigate('/parametres')}>
            Gerer les attributs <ChevronRight size={14} />
          </button>
        </header>
        <div className="card-body">
          <table className="data-table">
            <thead>
              <tr>
                <th>Attribut</th>
                <th>Valeur</th>
                <th>Visibilite</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(eq.attributs).map(([k, v]) => {
                const meta = attributsEquipements.find((a) => a.nom.toLowerCase() === k.toLowerCase());
                const isConf = meta?.confidentiel;
                return (
                  <tr key={k}>
                    <td><strong>{k}</strong></td>
                    <td>{isConf ? <span className="mono">***</span> : v}</td>
                    <td>
                      {isConf ? (
                        <span className="badge badge-warn"><EyeOff size={12} /> Confidentiel</span>
                      ) : (
                        <span className="badge badge-ok"><Eye size={12} /> Standard</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.25rem' }}>
        <header className="card-header">
          <div className="card-title">
            <Building2 size={18} />
            <h2>References fournisseurs ({eq.references.length})</h2>
          </div>
        </header>
        <div className="card-body">
          <table className="data-table">
            <thead>
              <tr>
                <th>Fournisseur</th>
                <th>Reference produit</th>
                <th>Delai</th>
                <th style={{ textAlign: 'right' }}>Prix d achat</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {eq.references.map((ref) => {
                const f = fournisseurs.find((x) => x.id === ref.fournisseurId);
                return (
                  <tr key={ref.fournisseurId} className="hoverable clickable" onClick={() => navigate(`/fournisseurs/${ref.fournisseurId}`)}>
                    <td>
                      <div className="cell-strong">
                        <Building2 size={14} className="cell-icon" />
                        <div>
                          <p>{f?.nom}</p>
                          <span className="meta-line">{f?.contact}</span>
                        </div>
                      </div>
                    </td>
                    <td><span className="mono">{ref.reference}</span></td>
                    <td>{ref.delai}</td>
                    <td style={{ textAlign: 'right' }}><strong>{formatMontant(ref.prixAchat)}</strong></td>
                    <td style={{ width: 28 }}><ChevronRight size={16} className="arrow-muted" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.25rem' }}>
        <header className="card-header">
          <div className="card-title">
            <ShoppingCart size={18} />
            <h2>Historique commandes ({commandesLiees.length})</h2>
          </div>
        </header>
        <div className="card-body">
          {commandesLiees.length === 0 ? (
            <div className="empty-state compact">
              <p>Aucune commande n est associee a cet equipement.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Commande</th>
                  <th>Date</th>
                  <th>Chantier</th>
                  <th style={{ textAlign: 'right' }}>Quantite</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {commandesLiees.map((cmd) => {
                  const ligne = cmd.lignes.find((l) => l.equipementId === eq.id);
                  return (
                    <tr key={cmd.id} className="hoverable clickable" onClick={() => navigate(`/commandes/${cmd.id}`)}>
                      <td><span className="mono">{cmd.id}</span></td>
                      <td>{formatDate(cmd.date)}</td>
                      <td>{cmd.chantier}</td>
                      <td style={{ textAlign: 'right' }}><strong>{ligne?.quantite}</strong></td>
                      <td style={{ width: 28 }}><ChevronRight size={16} className="arrow-muted" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default EquipementDetail;
